import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ILoginRequest,
  ILoginResponse,
  LoginRequest,
  IWhoAmIResponse,
  ChangePasswordRequest,
  IChangePasswordRequest,
  ForgotPasswordRequest,
  IForgotPasswordRequest,
  SafeDatabaseInstance,
  DirectNetUserType,
  DatabaseTargetPermission,
  SysPrin,
  ISaveLoginRedirectRequest,
  SaveLoginRedirectRequest,
} from '../api/client';
import Api from '../api/helpers';
import { IRequestState, IErrorDetails, instanceOfIErrorDetails, initialRequestState } from './interfaces';
import { customToast } from '../components/shared/Toasts';
import { toast } from 'react-toastify';
import { IPreferencesState, receivePersistedState, saveUserPreferences } from './preferencesSlice';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';

export interface IAuthState {
  loginRequest: IRequestState & { isCredsInvalid: boolean };
  changePwdRequest: IRequestState;
  forgotPasswordRequest: IRequestState;
  availableInstances: SafeDatabaseInstance[] | undefined;
  isSignedIn: boolean;
  isLoggingOut: boolean;
  isLoggingOutWithChanges: boolean;
  username: string | undefined;
  firstname: string | undefined;
  lastname: string | undefined;
  userEmail: string | undefined;
  isPassResetRequired: boolean;
  isFetchingWhoAmI: boolean;
  isNetworkError: boolean;
  usertype: DirectNetUserType;
  permissions: DatabaseTargetPermission[];
  permittedSysPrinsforExtUser: SysPrin[] | null;
  timeToAuthExpiry: number;
  dnetUserNamesByAzureEmail: string[] | undefined;
  isLoginRedirect: boolean;
  fullscreenErrorMsg: string | undefined;
  isNative: boolean | undefined;
}

const initialState: IAuthState = {
  isSignedIn: false,
  isLoggingOut: false,
  isLoggingOutWithChanges: false,
  availableInstances: undefined,
  username: undefined,
  firstname: undefined,
  lastname: undefined,
  userEmail: undefined,
  isPassResetRequired: false,
  isFetchingWhoAmI: true,
  isNetworkError: false,
  usertype: DirectNetUserType.External,
  permissions: [],
  permittedSysPrinsforExtUser: [],
  isLoginRedirect: false,
  isNative: undefined,
  loginRequest: {
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
    isModelInvalid: false,
    isCredsInvalid: false,
  },
  changePwdRequest: {
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
    isModelInvalid: false,
  },
  forgotPasswordRequest: {
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
    isModelInvalid: false,
  },
  timeToAuthExpiry: 0,
  dnetUserNamesByAzureEmail: undefined,
  fullscreenErrorMsg: undefined,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    handleNetworkError: (state) => {
      state.isNetworkError = true;
    },
    handleIsLoggingOut: (state) => {
      state.isLoggingOut = true;
    },
    handleConfirmLogout: (state) => {
      state.isLoggingOut = false;
    },
    handleWithChanges: (state) => {
      state.isLoggingOutWithChanges = true;
    },
    handleConfirmWithChanges: (state) => {
      state.isLoggingOutWithChanges = false;
    },
    clearErrors: (state) => {
      state.loginRequest.errorDetails = { statusCode: null, userMessage: null };
      state.changePwdRequest.errorDetails = { statusCode: null, userMessage: null };
      state.forgotPasswordRequest.errorDetails = { statusCode: null, userMessage: null };
      state.loginRequest.isCredsInvalid = false;
      state.loginRequest.isModelInvalid = false;
      state.changePwdRequest.isModelInvalid = false;
      state.forgotPasswordRequest.isModelInvalid = false;
    },
    handleTimeToAuthExpiry: (state, action: any) => {
      state.timeToAuthExpiry = action.payload;
    },
    setLoginRedirect: (state, action: PayloadAction<boolean>) => {
      state.isLoginRedirect = action.payload;
    },
    setNative: (state, action: PayloadAction<boolean | undefined>) => {
      state.isNative = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.pending, () => {
      return {
        ...initialState,
        isFetchingWhoAmI: false,
        loginRequest: { ...initialRequestState, isRequestActive: true, isCredsInvalid: false },
      };
    });
    builder.addCase(signIn.fulfilled, (state, action) => {
      state.loginRequest.isRequestActive = false;
      state.isSignedIn = true;
      state.username = action.payload.username;
      state.firstname = action.payload.firstName;
      state.lastname = action.payload.lastName;
      state.userEmail = action.payload.emailAddress;
      state.isPassResetRequired = action.payload.isPassResetRequired;
      state.availableInstances = action.payload.availableTargets;
      state.usertype = action.payload.usertype;
      state.isLoginRedirect = action.payload.isLoginRedirect;
      state.permissions = action.payload.permissions;
      state.permittedSysPrinsforExtUser = action.payload.userPermittedSysPrinsForExternalUser ?? null;
    });
    builder.addCase(signIn.rejected, (state, action) => {
      state.loginRequest.isRequestActive = false;
      if (!action.payload) return;
      switch (action.payload.statusCode) {
        case 401:
          state.loginRequest.isCredsInvalid = true;
          state.loginRequest.errorDetails = action.payload;
          break;
        case 400:
          state.loginRequest.isModelInvalid = true;
          state.loginRequest.errorDetails = action.payload;
          break;
        default:
          state.loginRequest.errorDetails = action.payload;
          break;
      }
    });
    builder.addCase(signOut.fulfilled, () => {
      return { ...initialState, isFetchingWhoAmI: false };
    });
    builder.addCase(whoAmI.pending, (state) => {
      state.isFetchingWhoAmI = true;
    });
    builder.addCase(whoAmI.fulfilled, (state, action) => {
      state.isFetchingWhoAmI = false;
      state.isPassResetRequired = action.payload.isPassResetRequired ?? false;
      state.isSignedIn = action.payload.isAuthenticated;
      if (action.payload.isAuthenticated) {
        state.username = action.payload.username;
        state.usertype = action.payload.userType;
        state.firstname = action.payload.firstName;
        state.lastname = action.payload.lastName;
        state.userEmail = action.payload.emailAddress;
        state.permissions = action.payload.permissions;
        state.isLoginRedirect = action.payload.isLoginRedirect;
      } else {
        state.username = undefined;
      }
      state.availableInstances = action.payload.availableTargets;
      state.dnetUserNamesByAzureEmail = action.payload.dnetUserNamesByAzureEmail;
      state.fullscreenErrorMsg = action.payload.fullscreenErrorMsg;
    });
    builder.addCase(changePassword.pending, (state) => {
      state.changePwdRequest = { ...initialRequestState, isRequestActive: true };
    });
    builder.addCase(changePassword.fulfilled, (state) => {
      if (state.isPassResetRequired) return { ...initialState, isFetchingWhoAmI: false };
      state.changePwdRequest.isRequestActive = false;
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      state.changePwdRequest.isRequestActive = false;
      if (!action.payload) return;
      if (action.payload.statusCode === 400) {
        state.changePwdRequest.isModelInvalid = true;
      }
      state.changePwdRequest.errorDetails = action.payload;
    });
    builder.addCase(forgotPassword.pending, (state) => {
      state.forgotPasswordRequest = { ...initialRequestState, isRequestActive: true };
    });
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.forgotPasswordRequest = { ...initialRequestState, isRequestActive: false };
    });
    builder.addCase(forgotPassword.rejected, (state, action) => {
      state.forgotPasswordRequest.isRequestActive = false;
      if (!action.payload) return;
      if (action.payload.statusCode === 400) {
        state.forgotPasswordRequest.isModelInvalid = true;
      }
      state.forgotPasswordRequest.errorDetails = action.payload;
    });
  },
});

export const whoAmI = createAsyncThunk<IWhoAmIResponse, string | undefined>('auth/whoAmI', async (user, thunkApi) => {
  const client = Api.getAuthClient(thunkApi.dispatch);
  const response = await client.whoAmI(user);

  if (response.isAuthenticated) {
    const preferences = localStorage.getItem('preferencesState');
    if (preferences) thunkApi.dispatch(receivePersistedState(JSON.parse(preferences) as IPreferencesState));
    else if (response.preferencesState) {
      const preferenceState = JSON.parse(response.preferencesState) as IPreferencesState;

      //Append trash bin grid state to user preferences
      if (!preferenceState.trashBinGridDataState)
        preferenceState.trashBinGridDataState = {
          filter: { filters: [] as CompositeFilterDescriptor[], logic: 'and' },
          skip: 0,
          take: 100,
        };

      thunkApi.dispatch(receivePersistedState(preferenceState));
    }
  }
  await client.getAntiforgeryToken();
  return response;
});

export const signIn = createAsyncThunk<
  ILoginResponse,
  { loginRequest: ILoginRequest; clearFormCallback: () => void },
  { rejectValue: IErrorDetails }
>('auth/signIn', async ({ loginRequest, clearFormCallback }, thunkApi) => {
  try {
    const client = Api.getAuthClient(thunkApi.dispatch);
    const response: ILoginResponse = await client.login(new LoginRequest(loginRequest));
    await client.getAntiforgeryToken();
    toast.dismiss();
    if (response.preferencesState) {
      const preferences = JSON.parse(response.preferencesState) as IPreferencesState;

      //Append trash bin grid state to user preferences
      if (!preferences.trashBinGridDataState)
        preferences.trashBinGridDataState = {
          filter: { filters: [] as CompositeFilterDescriptor[], logic: 'and' },
          skip: 0,
          take: 100,
        };

      thunkApi.dispatch(receivePersistedState(preferences));
    }

    if (response.isPasswordChangeRecommended) {
      customToast({
        content: response.passwordChangeMessage,
        type: 'warn',
        autoClose: true,
        id: 'passwordChangeRecommended',
      });
    }
    return response;
  } catch (err) {
    if (instanceOfIErrorDetails(err)) {
      if (err.statusCode === 401) {
        customToast({
          content:
            ' Incorrect username or password. There are a limited number of login attempts between DirectNet, Bill Composer, and InView before your account is locked out.',
          type: 'error',
          autoClose: false,
          isAuth: true,
          id: 'invalidCreds',
          dismissOnNavigation: true,
        });
      }
      return thunkApi.rejectWithValue(err);
    }
    return thunkApi.rejectWithValue({ statusCode: 500, userMessage: 'We are experiencing technical difficulties.' });
  } finally {
    clearFormCallback();
  }
});

export const signOut = createAsyncThunk('auth/signOut', async (_, thunkApi) => {
  thunkApi.dispatch(saveUserPreferences());
  const client = Api.getAuthClient(thunkApi.dispatch);
  await client.ssoLogout();
  await client.getAntiforgeryToken();
  return;
});

export const changePassword = createAsyncThunk<
  undefined,
  { changePasswordRequest: IChangePasswordRequest; clearFormCallback: () => void },
  { rejectValue: IErrorDetails }
>('auth/changePassword', async ({ changePasswordRequest, clearFormCallback }, thunkApi) => {
  try {
    const client = Api.getAuthClient(thunkApi.dispatch);
    await client.changePassword(new ChangePasswordRequest(changePasswordRequest));
    customToast({
      content: 'Password changed successfully.',
      type: 'success',
      autoClose: true,
      isAuth: true,
      id: 'validCredsChange',
    });
    clearFormCallback();
  } catch (err) {
    if (instanceOfIErrorDetails(err)) {
      return thunkApi.rejectWithValue(err);
    }
    return thunkApi.rejectWithValue({ statusCode: 500, userMessage: 'We are experiencing technical difficulties.' });
  }
});

export const forgotPassword = createAsyncThunk<
  undefined,
  { forgotPasswordRequest: IForgotPasswordRequest; onSuccessCallback: () => void },
  { rejectValue: IErrorDetails }
>('auth/forgotPassword', async ({ forgotPasswordRequest, onSuccessCallback }, thunkApi) => {
  try {
    const client = Api.getAuthClient(thunkApi.dispatch);
    await client.forgotPassword(new ForgotPasswordRequest(forgotPasswordRequest));
    customToast({
      content: 'Forgot Password submitted successfully.',
      type: 'success',
      autoClose: true,
      isAuth: true,
      id: 'forgotPasswordSuccess',
    });
    onSuccessCallback();
  } catch (err) {
    if (instanceOfIErrorDetails(err)) {
      return thunkApi.rejectWithValue(err);
    }
    return thunkApi.rejectWithValue({ statusCode: 500, userMessage: 'We are experiencing technical difficulties.' });
  }
});

export const saveLoginRedirect = createAsyncThunk<any, ISaveLoginRedirectRequest>(
  '/user/saveLoginRedirect',
  async (request, thunkApi) => {
    try {
      const userClient = Api.getUserClient(thunkApi.dispatch);
      const response = await userClient.saveLoginRedirect(new SaveLoginRedirectRequest(request));
      customToast({
        content: 'Login Redirect successfully saved.',
        type: 'success',
        autoClose: true,
        isAuth: false,
        id: 'clearLoginRedirectSuccess',
      });
      return response;
    } catch (err) {
      if (instanceOfIErrorDetails(err)) {
        return thunkApi.rejectWithValue(err);
      }
      return thunkApi.rejectWithValue({ statusCode: 500, userMessage: 'We are experiencing technical difficulties.' });
    }
  },
);

export const {
  handleNetworkError,
  handleIsLoggingOut,
  handleConfirmLogout,
  handleWithChanges,
  handleConfirmWithChanges,
  clearErrors,
  handleTimeToAuthExpiry,
  setLoginRedirect,
  setNative,
} = authSlice.actions;
export default authSlice.reducer;
